import React, { useContext } from "react";
import { FormControl, Grid, IconButton, useTheme } from "@mui/material";
import MenuCard from "../Components/MenuCard";
import { GlobalContext } from "../Utils/Contexts";
import Box from "@mui/material/Box";
import { TicketCrew } from "./Ticket/Crew/ticketCrew";
import { TicketCustomer } from "./Ticket/Customer/ticketCustomer";
import { TicketExtended } from "../Utils/TypeExtensions";
import CustomerMenuContent from "./Ticket/Customer/CustomerMenuContent";
import CrewMenuContent from "./Ticket/Crew/CrewMenuContent";
import PassengerMenuContent from "./Ticket/Passenger/PassengerMenuContent";
import { TicketPassengers } from "./Ticket/Passenger/ticketPassengers";
import { TicketFlightLegs, TicketProps } from "./Ticket/FlightLegs/ticketFlightLegs";
import FlightLegsMenuContent from "./Ticket/FlightLegs/FlightLegsMenuContent";
import FuelMenuContent from "./Ticket/Fuel/FuelMenuContent";
import { TicketFuel } from "./Ticket/Fuel/ticketFuel";
import LandingMenuContent from "./Ticket/Landing/LandingMenuContent";
import { TicketLandings } from "./Ticket/Landing/ticketLandings";
import NotesMenuContent from "./Ticket/Notes/NotesMenuContent";
import { TicketNotes } from "./Ticket/Notes/ticketNotes";
import { TicketCharges } from "./Ticket/Charges/ticketCharges";
import ChargesMenuContent from "./Ticket/Charges/ChargesMenuContent";
import MinimumsMenuContent from "./Ticket/Minimums/MinimumsMenuContent";
import { TicketMinimums } from "./Ticket/Minimums/ticketMinimums";
import TripTicketPdfMenuContent from "./Ticket/TripTicketPdf/TripTicketPdfMenuContent";
import { TicketPdf } from "./Ticket/TripTicketPdf/ticketPdf";
import CloseIcon from "@mui/icons-material/CancelRounded";

import TicketValidator, {
    CHARGES,
    CREW,
    CUSTOMER,
    FLIGHT_LEGS,
    FUEL,
    LANDINGS,
    MINIMUMS,
    NOTES,
    PASSENGERS,
    PER_DIEMS,
    SHOW_MENU,
    TRIP_TICKET_PDF,
} from "../Utils/TicketValidator";
import PerDiemsMenuContent from "./Ticket/PerDiems/PerDiemsMenuContext";
import { TicketPerDiems } from "./Ticket/PerDiems/ticketPerDiems";
import { createInitializationTicket, FilterLineItems, isInitializationTicket } from "../Utils/UtilityMethods";
import { FUEL_LINE_ITEM_TYPES, LANDING_LINE_ITEM_TYPES, MINIMUMS_VISIBLE_COMPANIES, OTHER_CHARGES_LINE_ITEM_TYPES, PER_DIEMS_LINE_ITEM_TYPES } from "../sharedCode/metadata";
import { findById, isMinimumsRequiredCompany } from "../Utils/ContextLookups";
import Typography from "@mui/material/Typography";
import { HasValue, IsDemoMode } from "../sharedCode/HelperMethods";
import { useLiveQuery } from "dexie-react-hooks";
import { saveLocalOnlyTicketChanges, TicketDatabase } from "../Utils/TicketDatabase";

interface MenuState {
    detailPage: string;
    ticket: TicketExtended;
}

export default function TicketPage(): JSX.Element {
    const context = useContext(GlobalContext);
    const theme = useTheme();

    const aircraftList = useLiveQuery(async () => TicketDatabase.aircraft.toArray());
    const ameList = useLiveQuery(async () => TicketDatabase.ame.toArray());
    const baseLocationList = useLiveQuery(async () => TicketDatabase.companyBase.toArray());
    const companyList = useLiveQuery(async () => TicketDatabase.company.toArray());
    const jobList = useLiveQuery(async () => TicketDatabase.job.toArray());
    const lineItemTypeList = useLiveQuery(async () => TicketDatabase.ticketLineItemType.toArray());
    const minimumsRequiredList = MINIMUMS_VISIBLE_COMPANIES;
    const pilotList = useLiveQuery(async () => TicketDatabase.pilot.toArray());
    const ticketList = useLiveQuery(async () => TicketDatabase.ticket.toArray());

    if (!aircraftList || !ameList || !baseLocationList || !companyList || !jobList || !lineItemTypeList || !minimumsRequiredList || !pilotList || !ticketList) {
        return <></>;
    }

    const ticket = ticketList.find((t) => t.id === context.selectedTicketId) ?? createInitializationTicket();

    if (isInitializationTicket(ticket)) {
        return <></>;
    }

    if (ticket.id < 0 && ticket.OpenedCards.findIndex((c) => c === context.openPage) < 0) {
        const openedCards = ticket.OpenedCards.slice();
        openedCards.push(context.openPage);
        saveLocalOnlyTicketChanges(ticket.id, { OpenedCards: openedCards }).catch(() => context.showForceReloadDialogue());
    }

    const errors = TicketValidator.validate(aircraftList, ticketList, ticket);

    const aircraftName = findById(aircraftList, ticket.aircraft_id)?.selectName || "";
    const companyName = findById(companyList, ticket.company_id)?.selectName || "";
    const jobName = ticket.job_id ? findById(jobList, ticket.job_id)?.label || "" : "";
    const pilotName = findById(pilotList, ticket.pilot_person_id)?.selectName || "";

    const openCard = (cardTitle: string) => {
        context.setOpenPage(cardTitle);
    };

    const closeDetail = () => {
        context.setOpenPage(SHOW_MENU);
    };

    let detailContent = <>Coming Soon</>;

    const isReadOnly = TicketValidator.IsReadOnly(ticket);
    const shouldShowDataDisclaimer = TicketValidator.ShouldShowDataDisclaimer(ticket, errors);

    const detailProps = {
        ticket: ticket,
        errors: errors,
        aircraftList: aircraftList,
        ameList: ameList,
        companyList: companyList,
        lineItemTypeList: lineItemTypeList,
        minimumsRequiredList: minimumsRequiredList,
        jobList: jobList,
        pilotList: pilotList,
        ticketList: ticketList,
        isReadOnly: isReadOnly,
        showForceReloadDialogue: context.showForceReloadDialogue,
    } as TicketProps;

    switch (context.openPage) {
        case CUSTOMER:
            detailContent = <TicketCustomer {...detailProps} />;
            break;
        case CREW:
            detailContent = <TicketCrew {...detailProps} />;
            break;
        case PASSENGERS:
            detailContent = <TicketPassengers key={`p-${ticket.Pax?.length}`} {...detailProps} />;
            break;
        case FLIGHT_LEGS:
            detailContent = <TicketFlightLegs {...detailProps} />;
            break;
        case CHARGES:
            detailContent = <TicketCharges key={`c-${ticket.LineItems?.length}`} {...detailProps} />;
            break;
        case PER_DIEMS:
            detailContent = <TicketPerDiems {...detailProps} />;
            break;
        case FUEL:
            detailContent = <TicketFuel key={`f-${ticket.LineItems?.length}`} {...detailProps} />;
            break;
        case LANDINGS:
            detailContent = <TicketLandings key={`l-${ticket.LineItems?.length}`} {...detailProps} />;
            break;
        case MINIMUMS:
            detailContent = <TicketMinimums {...detailProps} />;
            break;
        case NOTES:
            detailContent = <TicketNotes {...detailProps} />;
            break;
        case TRIP_TICKET_PDF:
            detailContent = <TicketPdf closeDialog={closeDetail} {...detailProps} />;
            break;
    }

    const hasBeenOpened = (cardName: string): boolean => {
        if (!ticket || !ticket.OpenedCards) return false;
        if (ticket.id >= 0) return true; // once synced, show everything as green (since the page opened flags aren't currently saved)
        return ticket?.OpenedCards.findIndex((c: string) => c === cardName) >= 0;
    };

    function isNoFlyDay() {
        return ticket.is_no_fly === undefined || ticket.is_no_fly === 1; // NB this flips the bool
    }

    const shouldShowCard = (cardName: string) => {
        switch (cardName) {
            case PASSENGERS:
                if (isNoFlyDay() && (ticket.Pax?.length ?? 0) === 0) {
                    return false;
                }
                break;
            case FLIGHT_LEGS:
                if (isNoFlyDay() && (ticket.Locations?.length ?? 0) === 0) {
                    return false;
                }
                break;
            case FUEL:
                if (isNoFlyDay() && FilterLineItems(ticket, FUEL_LINE_ITEM_TYPES).length === 0) {
                    return false;
                }
                break;
            case LANDINGS:
                if ((isNoFlyDay() || !ticket.any_landings) && FilterLineItems(ticket, LANDING_LINE_ITEM_TYPES).length === 0) {
                    return false;
                }
                break;
            case PER_DIEMS:
                if (!ticket.is_per_diems && FilterLineItems(ticket, PER_DIEMS_LINE_ITEM_TYPES).length === 0) {
                    return false;
                }
                break;
            case CHARGES:
                if (!ticket.is_other_charges && FilterLineItems(ticket, OTHER_CHARGES_LINE_ITEM_TYPES).length === 0) {
                    return false;
                }
                break;
            case MINIMUMS:
                return isMinimumsRequiredCompany(ticket.company_id);
            default:
                return true;
        }

        return true;
    };

    function handleEditFormHeaderClick() {
        // do nothing, just want to swallow all the clicks so they do pass thru to the control scrolled up underneath when the iPad kbd is hidden
    }

    return (
        <Box flex={1}>
            {ticket && context.openPage === SHOW_MENU && (
                <Grid container sx={{ paddingTop: 6, paddingLeft: 5, paddingRight: 5, paddingBottom: 5 }} spacing={3} justifyItems={"center"} alignItems={"center"}>
                    <MenuCard
                        openCard={openCard}
                        title={CUSTOMER}
                        menuCardContent={CustomerMenuContent(ticket, CUSTOMER, companyName, jobName, errors)}
                        isOpened={hasBeenOpened(CUSTOMER)}
                        isReadOnly={isReadOnly}
                    />
                    {shouldShowCard(CREW) && (
                        <MenuCard
                            openCard={openCard}
                            title={CREW}
                            menuCardContent={CrewMenuContent(CREW, pilotName, aircraftName, errors)}
                            isOpened={hasBeenOpened(CREW)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                    {shouldShowCard(PASSENGERS) && (
                        <MenuCard
                            openCard={openCard}
                            title={PASSENGERS}
                            menuCardContent={PassengerMenuContent(ticket, PASSENGERS, errors)}
                            isOpened={hasBeenOpened(PASSENGERS)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                    {shouldShowCard(FLIGHT_LEGS) && (
                        <MenuCard
                            openCard={openCard}
                            title={FLIGHT_LEGS}
                            menuCardContent={FlightLegsMenuContent(ticket, FLIGHT_LEGS, errors)}
                            isOpened={hasBeenOpened(FLIGHT_LEGS)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                    {shouldShowCard(FUEL) && (
                        <MenuCard openCard={openCard} title={FUEL} menuCardContent={FuelMenuContent(ticket, FUEL, errors)} isOpened={hasBeenOpened(FUEL)} isReadOnly={isReadOnly} />
                    )}
                    {shouldShowCard(LANDINGS) && (
                        <MenuCard
                            openCard={openCard}
                            title={LANDINGS}
                            menuCardContent={LandingMenuContent(ticket, LANDINGS, errors)}
                            isOpened={hasBeenOpened(LANDINGS)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                    {shouldShowCard(PER_DIEMS) && (
                        <MenuCard
                            openCard={openCard}
                            title={PER_DIEMS}
                            menuCardContent={PerDiemsMenuContent(ticket, PER_DIEMS, errors)}
                            isOpened={hasBeenOpened(PER_DIEMS)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                    {shouldShowCard(CHARGES) && (
                        <MenuCard
                            openCard={openCard}
                            title={CHARGES}
                            menuCardContent={ChargesMenuContent(ticket, CHARGES, errors)}
                            isOpened={hasBeenOpened(CHARGES)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                    {shouldShowCard(MINIMUMS) && (
                        <MenuCard
                            openCard={openCard}
                            title={MINIMUMS}
                            menuCardContent={MinimumsMenuContent(ticket, MINIMUMS, errors)}
                            isOpened={hasBeenOpened(MINIMUMS)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                    {shouldShowCard(NOTES) && (
                        <MenuCard
                            openCard={openCard}
                            title={NOTES}
                            menuCardContent={NotesMenuContent(ticket, NOTES, errors)}
                            isOpened={hasBeenOpened(NOTES)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                    {shouldShowCard(TRIP_TICKET_PDF) && (
                        <MenuCard
                            openCard={openCard}
                            title={TRIP_TICKET_PDF}
                            menuCardContent={TripTicketPdfMenuContent(ticket, TRIP_TICKET_PDF, errors)}
                            isOpened={hasBeenOpened(TRIP_TICKET_PDF)}
                            isReadOnly={isReadOnly}
                        />
                    )}
                </Grid>
            )}
            {ticket && context.openPage !== SHOW_MENU && (
                <Grid container flexDirection={"column"}>
                    <Box
                        sx={{
                            flexGrow: 1,
                            paddingTop: 0,
                            paddingLeft: 3,
                            paddingRight: 3,
                            marginTop: 2,
                            marginLeft: 3,
                            marginRight: 3,
                            marginBottom: 1,
                            width: "96vw",
                            backgroundColor: shouldShowDataDisclaimer ? theme.palette.error.light : theme.palette.background.default,
                            zIndex: 10,
                        }}>
                        <Grid onClick={handleEditFormHeaderClick} container item sx={{ paddingBottom: 1, marginTop: 2, marginRight: 1 }} spacing={1} alignItems={"center"}>
                            <Grid item flex={1} container>
                                <Typography
                                    color={shouldShowDataDisclaimer ? theme.palette.text.primary : theme.palette.primary.main}
                                    fontSize={"larger"}
                                    sx={{ paddingTop: 2, paddingBottom: 2 }}>
                                    {shouldShowDataDisclaimer ? "DATA INCOMPLETE - REFER TO ORIGINAL EMAILED PDF" : context.openPage}
                                </Typography>
                            </Grid>
                            {(HasValue(ticket.job_id) || IsDemoMode()) && (
                                <Grid item flex={0} container>
                                    <IconButton onClick={closeDetail} size={"large"} edge={"end"} sx={{ color: theme.palette.primary.main, marginRight: 1 }}>
                                        <CloseIcon fontSize={"large"} />
                                    </IconButton>
                                </Grid>
                            )}
                        </Grid>
                        <Grid container item alignItems={"center"} spacing={1} sx={{ marginTop: 1 }}>
                            <FormControl
                                fullWidth
                                sx={{
                                    marginLeft: 1,
                                    marginRight: 1,
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                }}
                            />
                        </Grid>
                    </Box>
                    <Box
                        sx={{
                            // border: "1px solid lightgrey",
                            // borderRadius: 3,
                            paddingTop: 2,
                            paddingLeft: 3,
                            paddingRight: 3,
                            paddingBottom: 3,
                            // marginTop: 8,
                            marginLeft: 3,
                            marginRight: 3,
                            marginBottom: 3,
                            // backgroundColor: theme.palette.background.paper,
                        }}>
                        <Grid flex={1} item>
                            {detailContent}
                        </Grid>
                    </Box>
                </Grid>
            )}
        </Box>
    );
}
